import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getAxiosInstance } from '../../../axiosInstances/axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { City, CityReqBody } from '../../../types/cities';
import { AcademiaResponse } from '../../../types/common';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useCitiesActions = (
  whichAction: 'cityList' | '',
  provinceId?: number
) => {
  // query function to fetch all the cities from database declared here
  const getCityListHandler = useQuery({
    queryKey: ['city-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<City>>> => {
      const response = await getPrivateAxiosInstance('user').get('/city/list/');

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'cityList'),
  });

  const getProvinceWiseCitiesHandler = useQuery({
    queryKey: ['province-wise-cities', provinceId],
    queryFn: async (): Promise<AcademiaResponse<Array<City>>> => {
      const response = await getAxiosInstance('user').get(
        `/cities/with/${provinceId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!provinceId),
  });

  // mutation function to add new city into the database declared here
  const addNewCityHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: Array<CityReqBody>;
    }): Promise<AcademiaResponse<City>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/city/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to update existing city by id declared here
  const updateCityByIdHandler = useMutation({
    mutationFn: async ({
      cityId,
      body,
    }: {
      cityId: number;
      body: CityReqBody;
    }): Promise<AcademiaResponse<City>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/city/${cityId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to delete city by id declared here
  const deleteCityByIdHandler = useMutation({
    mutationFn: ({
      cityId,
    }: {
      cityId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(`/city/${cityId}/delete/`),
  });

  return {
    getCityListHandler,
    getProvinceWiseCitiesHandler,
    addNewCityHandler,
    updateCityByIdHandler,
    deleteCityByIdHandler,
  };
};

export default useCitiesActions;
